import { useState } from "react";
import { motion } from "motion/react";
import { ArrowRight, Play } from "lucide-react";
import { siteData } from "../../data/mock";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const spring = { type: "spring", damping: 80, stiffness: 400, mass: 1 };

export const HeroSection = () => {
  const { hero } = siteData;
  const [email, setEmail] = useState("");

  const handleDemo = async (e) => {
    e.preventDefault();
    if (email.trim()) {
      try {
        await axios.post(`${BACKEND_URL}/api/demo-requests`, {
          name: "Quick Demo",
          email: email.trim(),
          business: "",
        });
        toast.success("Demo request received! We'll be in touch soon.");
        setEmail("");
      } catch {
        toast.error("Something went wrong. Please try again.");
      }
    }
  };

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Gradient Background */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, rgba(217,138,140,0.15) 0%, rgba(225,197,103,0.12) 40%, rgba(223,181,115,0.1) 100%)",
          }}
        />
        {/* Decorative blobs */}
        <div
          className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-20 blur-3xl"
          style={{ background: "radial-gradient(circle, #D98A8C, transparent 70%)" }}
        />
        <div
          className="absolute -bottom-20 -left-20 w-[400px] h-[400px] rounded-full opacity-15 blur-3xl"
          style={{ background: "radial-gradient(circle, #E1C567, transparent 70%)" }}
        />
      </div>

      <div className="relative z-10 max-w-[1200px] mx-auto px-6 md:px-8 pt-28 pb-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...spring, delay: 0.1 }}
              className="inline-flex items-center gap-2 bg-[#008055]/10 text-[#008055] rounded-full px-4 py-2 text-sm font-medium mb-6"
            >
              <span className="w-2 h-2 rounded-full bg-[#008055] animate-pulse" />
              Loyalty Growth Engine
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...spring, delay: 0.2 }}
              className="text-[#0A0A0A] leading-[1.1] tracking-tight mb-6"
              style={{ fontSize: "clamp(36px, 5vw, 56px)", fontWeight: 500 }}
            >
              {hero.headline}
              <br />
              <span className="text-[#008055]">{hero.headlineAccent}</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...spring, delay: 0.3 }}
              className="text-[#3C3B3D] text-lg leading-relaxed mb-8 max-w-[520px]"
            >
              {hero.subheadline}
            </motion.p>

            {/* Email + CTA */}
            <motion.form
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...spring, delay: 0.4 }}
              onSubmit={handleDemo}
              className="flex flex-col sm:flex-row gap-3 mb-6"
            >
              <div className="relative flex-1 max-w-[420px]">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your business email"
                  className="w-full h-[52px] px-5 pr-[140px] border border-[#CBC9CF] rounded-full text-base bg-white text-[#0A0A0A] outline-none focus:border-[#008055] placeholder:text-[#AFADB2] transition-colors duration-200"
                />
                <button
                  type="submit"
                  className="absolute top-[6px] right-[6px] bottom-[6px] bg-[#0A6647] hover:bg-[#008055] text-white border-none rounded-full px-5 text-sm font-normal cursor-pointer flex items-center gap-2 transition-colors duration-200"
                >
                  {hero.ctaPrimary}
                  <ArrowRight size={16} />
                </button>
              </div>
            </motion.form>

            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              onClick={() => scrollTo("#how-it-works")}
              className="flex items-center gap-2 text-[#3C3B3D] hover:text-[#008055] text-sm bg-transparent border-none cursor-pointer transition-colors duration-200"
            >
              <div className="w-8 h-8 rounded-full border border-[#CBC9CF] flex items-center justify-center">
                <Play size={14} className="ml-0.5" />
              </div>
              {hero.ctaSecondary}
            </motion.button>
          </div>

          {/* Right - Phone Mockup */}
          <motion.div
            initial={{ opacity: 0, x: 60, rotate: 3 }}
            animate={{ opacity: 1, x: 0, rotate: 0 }}
            transition={{ ...spring, delay: 0.3 }}
            className="relative flex justify-center lg:justify-end"
          >
            <div className="relative">
              {/* Phone frame */}
              <div className="w-[280px] md:w-[320px] rounded-[36px] overflow-hidden shadow-2xl shadow-black/10 border-[8px] border-[#1a1a1a] bg-[#1a1a1a]">
                <div className="w-full aspect-[9/19] bg-gradient-to-b from-[#f0f7f4] to-white relative overflow-hidden">
                  {/* Simulated app screen */}
                  <div className="p-5 pt-8">
                    <div className="flex items-center gap-2 mb-6">
                      <div className="w-8 h-8 rounded-lg bg-[#008055] flex items-center justify-center">
                        <span className="text-white text-xs font-semibold">P2</span>
                      </div>
                      <span className="text-[#0A0A0A] text-sm font-medium">Your Brand</span>
                    </div>
                    <div className="bg-[#008055]/10 rounded-2xl p-4 mb-4">
                      <p className="text-[#008055] text-xs font-medium mb-1">Your Points</p>
                      <p className="text-[#0A0A0A] text-2xl font-semibold">2,450</p>
                      <div className="mt-2 h-2 bg-[#008055]/20 rounded-full overflow-hidden">
                        <div className="h-full w-[72%] bg-[#008055] rounded-full" />
                      </div>
                      <p className="text-[#757478] text-xs mt-1">550 more to next reward</p>
                    </div>
                    <div className="space-y-3">
                      <div className="bg-white rounded-xl p-3 shadow-sm border border-[#E6E4EB]">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[#0A0A0A] text-sm font-medium">Free Coffee</p>
                            <p className="text-[#757478] text-xs">3,000 points</p>
                          </div>
                          <div className="w-8 h-8 rounded-full bg-[#DFB573]/20 flex items-center justify-center">
                            <span className="text-[#DFB573] text-sm">☕</span>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white rounded-xl p-3 shadow-sm border border-[#E6E4EB]">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[#0A0A0A] text-sm font-medium">20% Off</p>
                            <p className="text-[#757478] text-xs">5,000 points</p>
                          </div>
                          <div className="w-8 h-8 rounded-full bg-[#D98A8C]/20 flex items-center justify-center">
                            <span className="text-[#D98A8C] text-sm">✨</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Floating badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.8, type: "spring", stiffness: 300 }}
                className="absolute -left-8 top-1/3 bg-white rounded-2xl p-3 shadow-lg shadow-black/8 border border-[#E6E4EB]"
              >
                <p className="text-[#008055] text-xs font-medium">+50 points</p>
                <p className="text-[#757478] text-[10px]">Just earned!</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1, type: "spring", stiffness: 300 }}
                className="absolute -right-6 bottom-1/4 bg-white rounded-2xl p-3 shadow-lg shadow-black/8 border border-[#E6E4EB]"
              >
                <p className="text-[#0A0A0A] text-xs font-medium">Reward Ready!</p>
                <p className="text-[#757478] text-[10px]">Tap to redeem</p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
